import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import keyboardImage from '../assets/keyboard.webp';

const text = 'futureshift';

const keyPositions = {
  f: { x: 26.5, y: 53 },
  u: { x: 37, y: 40 },
  t: { x: 29.5, y: 40 },
  r: { x: 25.5, y: 40 },
  e: { x: 21.5, y: 40 },
  s: { x: 18, y: 53 },
  h: { x: 35, y: 53 },
  i: { x: 42, y: 40 },
  shift: { x: 52, y: 66, width: 11 },
};

const LandingPage = () => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentKeyIndex, setCurrentKeyIndex] = useState(-1);
  const [startTyping, setStartTyping] = useState(false);
  const [shiftPressed, setShiftPressed] = useState(false);

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error | duplicate

  useEffect(() => {
    const t = setTimeout(() => setStartTyping(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!startTyping) return;
    if (currentKeyIndex < text.length) {
      const t = setTimeout(() => {
        setDisplayedText(text.slice(0, currentKeyIndex + 2));
        setCurrentKeyIndex((i) => i + 1);
      }, 300);
      return () => clearTimeout(t);
    } else if (currentKeyIndex === text.length) {
      const t = setTimeout(() => setShiftPressed(true), 500);
      return () => clearTimeout(t);
    }
  }, [currentKeyIndex, startTyping]);

  const getDisplayText = () => {
    if (shiftPressed) {
      return (
        <>
          {displayedText}
          <motion.span
            className="inline-flex items-center justify-center mx-1 px-2 bg-gray-200 border border-gray-400 rounded align-middle"
            style={{ width: '2.5em', padding: '0.5rem 0.5rem' }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 14h5v6h6v-6h5L12 4z" />
            </svg>
          </motion.span>
        </>
      );
    }
    return displayedText;
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');

    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white overflow-hidden">
      {/* Brand */}
      <motion.div
        className="mb-16 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1 className="text-6xl font-bold text-black mb-4 flex items-center justify-center">
          <span className="text-black">{getDisplayText()}</span>
          <motion.span
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
            className="text-black inline-flex flex-col items-center justify-center ml-1"
            style={{ height: '0.85em', position: 'relative', top: '0.08em' }}
          >
            <span className="h-[1.5px] bg-black" style={{ width: '8px' }} />
            <span className="bg-black flex-1" style={{ width: '1.5px' }} />
            <span className="h-[1.5px] bg-black" style={{ width: '8px' }} />
          </motion.span>
        </h1>
        <p className="text-gray-700 text-sm tracking-widest uppercase">
          intelligence for those adapting early.
        </p>
      </motion.div>

      {/* Keyboard */}
      <motion.div
        className="relative w-full max-w-5xl px-8"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
      >
        <img src={keyboardImage} alt="Mechanical Keyboard" className="w-full h-auto" />

        {currentKeyIndex >= 0 && currentKeyIndex < text.length && (
          <motion.div
            key={currentKeyIndex}
            className="absolute w-[3.5%] h-[11%] bg-black rounded-sm"
            style={{
              left: `${keyPositions[text[currentKeyIndex]]?.x}%`,
              top: `${keyPositions[text[currentKeyIndex]]?.y}%`,
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: [0.8, 0], scale: [1, 1.2] }}
            transition={{ duration: 0.4 }}
          />
        )}

        {shiftPressed && (
          <motion.div
            key="shift"
            className="absolute h-[9%] bg-black rounded-sm"
            style={{
              left: `${keyPositions.shift.x}%`,
              top: `${keyPositions.shift.y}%`,
              width: `${keyPositions.shift.width}%`,
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: [0.8, 0], scale: [1, 1.2] }}
            transition={{ duration: 0.4 }}
          />
        )}
      </motion.div>

      {/* Waitlist */}
      <motion.div
        className="mt-24 w-full max-w-2xl px-8 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.8 }}
      >
        <p className="text-gray-600 text-lg mb-6">get early access.</p>

        {status === 'success' ? (
          <motion.p
            className="text-black text-base"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            you're on the list. we'll be in touch.
          </motion.p>
        ) : (
          <form onSubmit={handleWaitlist} className="flex gap-3 mb-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
              placeholder="your email"
              className="flex-1 px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-black text-black"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-6 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {status === 'loading' ? '...' : 'notify me'}
            </button>
          </form>
        )}

        {status === 'duplicate' && (
          <p className="text-gray-500 text-xs mt-2">you're already on the list.</p>
        )}
        {status === 'error' && (
          <p className="text-gray-500 text-xs mt-2">something went wrong. try again.</p>
        )}
        {status === 'idle' && (
          <p className="text-gray-500 text-xs mt-2">no spam. just signal.</p>
        )}
      </motion.div>
    </div>
  );
};

export default LandingPage;
